/**
 * League Context
 *
 * Global state for the user's leagues list.
 * Loaded once on app start, shared across Sidebar + all pages.
 *
 * Also tracks the "active league" — the one currently selected
 * for chat context.
 */

import { createContext, useContext, useState, useEffect, useRef } from 'react'
import { leaguesApi } from '../services/api'
import { useAuth } from './AuthContext'
import { setupPushNotifications } from '../services/pushNotifications'

const LeagueContext = createContext(null)

export function LeagueProvider({ children }) {
  const { user } = useAuth()
  const [leagues, setLeagues] = useState([])
  const [activeLeagueId, setActiveLeagueId] = useState(null)
  const [loading, setLoading] = useState(false)

  // The default league (Settings → Preferences) is applied ONCE per session,
  // on the first load of the list. Without this guard, every later refetch —
  // adding a league, syncing one — would drag the user back to their default
  // and undo a switch they just made on purpose. Choosing "General" has to
  // stick too, and that reads as activeLeagueId === null.
  const defaultApplied = useRef(false)

  // Push notifications (native only — see services/pushNotifications.js) are
  // requested the moment there's a real reason to want them: signed in AND
  // at least one connected league, never at welcome/login. Guarded so the
  // permission prompt fires at most once per session, not on every league
  // list refetch (adding a second league shouldn't re-prompt).
  const pushSetupRequested = useRef(false)

  // Fetch leagues when the signed-in user changes.
  //
  // Keyed on the id, not the user object: saving anything in Settings replaces
  // that object, and depending on it would refetch the whole league list every
  // time someone edited their name or nudged an advice setting.
  useEffect(() => {
    if (user) {
      fetchLeagues()
    } else {
      setLeagues([])
      setActiveLeagueId(null)
      defaultApplied.current = false   // next login gets its default again
      pushSetupRequested.current = false
    }
  }, [user?.id])

  const fetchLeagues = async () => {
    setLoading(true)
    try {
      const res = await leaguesApi.getAll()
      const list = res.data.leagues || []
      setLeagues(list)

      if (list.length > 0 && !pushSetupRequested.current) {
        pushSetupRequested.current = true
        setupPushNotifications()
      }

      // Open on the user's chosen league — but only if it's still one of
      // theirs. A default pointing at a league they since removed should
      // quietly do nothing rather than select something arbitrary.
      if (!defaultApplied.current) {
        defaultApplied.current = true
        const preferred = user?.defaultLeagueId
        if (preferred && list.some(l => l.id === preferred)) {
          setActiveLeagueId(preferred)
        }
      }
    } catch (err) {
      console.error('Failed to fetch leagues:', err)
    } finally {
      setLoading(false)
    }
  }

  const addLeague = (league) => {
    setLeagues(prev => [league, ...prev])
  }

  const removeLeague = (id) => {
    setLeagues(prev => prev.filter(l => l.id !== id))
    if (activeLeagueId === id) setActiveLeagueId(null)
  }

  const updateLeague = (id, updates) => {
    setLeagues(prev => prev.map(l => l.id === id ? { ...l, ...updates } : l))
  }

  const activeLeague = leagues.find(l => l.id === activeLeagueId) || null

  return (
    <LeagueContext.Provider value={{
      leagues,
      loading,
      activeLeagueId,
      activeLeague,
      setActiveLeagueId,
      addLeague,
      removeLeague,
      updateLeague,
      refetch: fetchLeagues,
    }}>
      {children}
    </LeagueContext.Provider>
  )
}

export function useLeagues() {
  const ctx = useContext(LeagueContext)
  if (!ctx) throw new Error('useLeagues must be used inside <LeagueProvider>')
  return ctx
}
